import React from "react";
import { RefreshCw, Sun, Moon, ChevronLeft, ChevronRight, LucideIcon } from "lucide-react";
import { cn } from "../../utils/cn";
import { viewOptions, ViewOption } from "../../utils/constants";

interface SidebarProps {
    isMobile: boolean;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    activeView: string;
    setActiveView: (view: string) => void;
    isDarkMode: boolean;
    setIsDarkMode: (isDark: boolean) => void;
    isAuthenticated: boolean;
    onSync?: () => void;
    isAdmin: boolean;
    canAccessPage: (page: string) => boolean;
}

export function Sidebar({
    isMobile,
    isOpen,
    setIsOpen,
    activeView,
    setActiveView,
    isDarkMode,
    setIsDarkMode,
    isAuthenticated,
    onSync,
    isAdmin,
    canAccessPage
}: SidebarProps) {
    if (isMobile) return null;

    return (
        <aside
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
            className={cn(
                "flex-shrink-0 flex flex-col transition-all duration-300 ease-out",
                "bg-siemens-deep-blue-8 dark:bg-siemens-elevation-base-1",
                "border-r border-siemens-deep-blue/10 dark:border-siemens-divider",
                isOpen ? "w-64" : "w-16"
            )}>
            {/* Header */}
            <div className="h-14 flex items-center px-3 border-b border-siemens-deep-blue/10 dark:border-siemens-divider">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                        <img src="/logo.png" alt="ZPS Logo" className="w-full h-full object-contain" />
                    </div>
                    <div className={cn("overflow-hidden transition-all duration-300", isOpen ? "opacity-100 w-auto" : "opacity-0 w-0")}>
                        <h1 className="text-sm font-bold text-siemens-deep-blue dark:text-siemens-text-standard whitespace-nowrap">ZPS Innovation</h1>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 py-3 px-2 space-y-1 overflow-y-auto">
                {viewOptions
                    .filter((view: ViewOption) => canAccessPage(view.id))
                    .map((view: ViewOption) => {
                        const Icon = view.icon as LucideIcon;
                        return (
                            <button
                                key={view.id}
                                onClick={() => setActiveView(view.id)}
                                title={!isOpen ? view.label : undefined}
                                className={cn(
                                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150",
                                    "hover:bg-siemens-deep-blue/5 dark:hover:bg-siemens-elevation-selected",
                                    activeView === view.id
                                        ? "bg-siemens-petrol/10 text-siemens-petrol dark:bg-siemens-petrol-light/10 dark:text-siemens-petrol-light font-semibold"
                                        : "text-siemens-deep-blue-80 dark:text-siemens-text-soft"
                                )}
                            >
                                <span className={cn("flex-shrink-0 w-5 h-5 flex items-center justify-center", activeView === view.id && "text-siemens-petrol dark:text-siemens-petrol-light")}>
                                    <Icon className="w-4 h-4" />
                                </span>
                                <span className={cn("whitespace-nowrap overflow-hidden transition-all duration-300", isOpen ? "opacity-100" : "opacity-0 w-0")}>{view.label}</span>
                            </button>
                        )
                    })}
            </nav>

            {/* Bottom Actions */}
            <div className="py-3 px-2 border-t border-siemens-deep-blue/10 dark:border-siemens-divider space-y-1">
                {isOpen && isAuthenticated && onSync && (
                    <button onClick={onSync} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-siemens-petrol dark:text-siemens-petrol-light bg-siemens-petrol/10 hover:bg-siemens-petrol/20 transition-all font-semibold">
                        <RefreshCw className="w-5 h-5 flex-shrink-0" />
                        <span className="whitespace-nowrap">Sync to Cloud</span>
                    </button>
                )}

                <button onClick={() => setIsDarkMode(!isDarkMode)} className={cn("w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 text-siemens-deep-blue-80 dark:text-siemens-text-soft hover:bg-siemens-deep-blue/5 dark:hover:bg-siemens-elevation-selected")}>
                    <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">{isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</span>
                    <span className={cn("whitespace-nowrap overflow-hidden transition-all duration-300", isOpen ? "opacity-100" : "opacity-0 w-0")}>{isDarkMode ? "Light Mode" : "Dark Mode"}</span>
                </button>

                <div className={cn("text-[10px] text-center text-siemens-text-soft/50 py-1 transition-all", isOpen ? "opacity-100" : "opacity-0 h-0 overflow-hidden")}>v3.0.0 (Ent)</div>

                <button onClick={() => setIsOpen(!isOpen)} className={cn("w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 text-siemens-deep-blue-80 dark:text-siemens-text-soft hover:bg-siemens-deep-blue/5 dark:hover:bg-siemens-elevation-selected")}>
                    <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">{isOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</span>
                    <span className={cn("whitespace-nowrap overflow-hidden transition-all duration-300", isOpen ? "opacity-100" : "opacity-0 w-0")}>Collapse</span>
                </button>
            </div>
        </aside>
    );
}

export default Sidebar;
